import React, { useRef, useEffect, useState } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, Tooltip, TooltipProps } from 'recharts';
import DatePicker from 'react-datepicker';
import { db } from "../../firebaseConfig";
import { useAuth } from '../../AuthContext';
import { useAppSelector } from '../../redux/store';
import { fetchLeadSourceAnalytics, LeadData as ImportedLeadData } from './Function';
import getCurrentScreen from '../../Helper/GetScreen';

type SourceData = {
  sourceId: string;
  source: string;
  count: number;
  potentialCount: number;
  percentage: string;
  potentialpercentage: string;
};

type NormalizedSourceData = Omit<SourceData, 'count' | 'potentialCount'> & {
  originalCount: number;
  originalPotential: number;
  count: number;
  potentialCount: number;
};

type NormalizedLeadData = Omit<ImportedLeadData, 'sourceMapArray'> & {
  sourceMapArray: (SourceData | NormalizedSourceData)[];
};

interface UserOption {
  id: string;
  name: string;
}

interface SourceLeadAnalyticsProps {
  users: UserOption[];
  settings: any;
}

const ChannelAnalytics: React.FC<SourceLeadAnalyticsProps> = ({ users, settings }) => {
  const { userData, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const [customDatePopupOpen, setCustomDatePopupOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filter, setFilter] = useState<boolean>(false);
  const customDatePopupRef = useRef<HTMLDivElement>(null);

  const [leadData, setLeadData] = useState<NormalizedLeadData>();

  useEffect(() => {
    setStartDate(new Date());
    setEndDate(new Date());
  }, []);  // Only run once on mount since we're just setting initial dates

  const handleDateRange = (date: Date | null, type: string) => {
    if (!date) return null;

    if (type === 'start') {
      date.setHours(5, 30, 0, 0);
    } else {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(5, 29, 59, 0);
      return nextDay.toISOString();
    }

    return date.toISOString();
  };

  const handleDateRangeClick = () => {
    setCustomDatePopupOpen(!customDatePopupOpen);
  };

  const handleApplyFilters = () => {
    setFilter(true);
    setCustomDatePopupOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
        setCustomDatePopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizeData = (data: ImportedLeadData | undefined): NormalizedLeadData | undefined => {
    if (!data) return undefined;

    const sourceMapArray = data.sourceMapArray || [];

    // Get max total value for scaling
    const maxTotal = Math.max(...sourceMapArray.map(item => item.count));

    if (maxTotal === 0) {
      return {
        ...data,
        sourceMapArray: (data.sourceMapArray || []).map(item => ({
          ...item,
          originalCount: item.count,
          originalPotential: item.potentialCount,
          count: 0,
          potentialCount: 0
        })) as (SourceData | NormalizedSourceData)[]
      };
    }

    // Custom scaling function that uses the same scaling for both total and potential values
    const scaleValue = (value: number): number => {
      if (value === 0) return 0;

      // Use cube root for better distribution of large differences
      const normalizedValue = Math.cbrt(value) / Math.cbrt(maxTotal);
      return Number((normalizedValue * 100).toFixed(1));
    };

    // Store original values for display
    const normalizedData: NormalizedLeadData = {
      ...data,
      sourceMapArray: (data.sourceMapArray || []).map((item: SourceData) => ({
        ...item,
        originalCount: item.count,
        originalPotential: item.potentialCount,
        count: scaleValue(item.count),
        potentialCount: scaleValue(item.potentialCount)
      })) as (SourceData | NormalizedSourceData)[]
    };

    return normalizedData;
  };

  useEffect(() => {
    fetchLeadSourceAnalytics({
      db,
      userData,
      startTime: handleDateRange(startDate, 'start') || null,
      endTime: handleDateRange(endDate, 'end') || null,
      users,
      currentScreen,
      settingsData: settings?.sources
    })
      .then((data) => setLeadData(normalizeData(data)))
      .catch((error) => console.error("Error fetching source analytics:", error));
  }, [userData?.companyId, startDate, endDate, users, currentScreen, filter]);

  return (
    <div className="bg-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
      <div className="mx-auto bg-white p-6 rounded-xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full"
              style={{
                background: "linear-gradient(180deg, #FFA782 0%, #FC583D 100%)",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/step.svg`}
                alt="Icon"
                className="w-6 h-6"
              />
            </div>
            <h2
              className="text-xl text-black font-medium"
              style={{
                fontFamily: "Montserrat",
                lineHeight: "29.26px",
              }}
            >
              Source Analytics
            </h2>
          </div>

          <div className="relative">
            <div
              className="w-full sm:w-auto py-2 px-1 border border-cyan-600 text-sm rounded-lg bg-white cursor-pointer"
              onClick={handleDateRangeClick}
            >
              {startDate === null && endDate === null
                ? "Lifetime"
                : startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : "Select Date Range"}
            </div>

            {customDatePopupOpen && (
              <div
                ref={customDatePopupRef}
                className="absolute top-12 right-0 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                style={{ minWidth: '320px' }}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[14px] font-normal leading-[20px] text-left">
                    Select Date Range
                  </h3>
                </div>
                <div className="mb-1">
                  <span className="text-[12px] font-normal leading-[16px] text-left">
                    <span className='font-medium'>Current: </span>
                    {startDate && endDate
                      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : "Select Date Range"}
                  </span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button onClick={() => { setStartDate(new Date()); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 7))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 30))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                    <button onClick={() => { setStartDate(new Date(new Date().setDate(new Date().getDate() - 90))); setEndDate(new Date()); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                    {/* Lifetime filter */}
                    <button onClick={() => { setStartDate(null); setEndDate(null); }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Lifetime</button>
                  </div>
                  <div className="p-4 bg-white shadow-lg rounded-[12px]">
                    <DatePicker
                      selected={startDate}
                      startDate={startDate || undefined}
                      endDate={endDate || undefined}
                      selectsRange
                      onChange={(update) => {
                        const [start, end] = update;
                        setStartDate(start);
                        setEndDate(end);
                      }}
                      inline
                      className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
                <div className="flex justify-between mt-2">
                  <button
                    className="px-8 py-2 text-white bg-black rounded-full"
                    onClick={() => setCustomDatePopupOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 min-h-[400px]">
          <div className="w-full lg:w-2/3 flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 transition-all duration-300 hover:bg-gray-100">
            <RadarChart
              width={window.innerWidth < 1024 ? 350 : 550}
              height={window.innerWidth < 1024 ? 300 : 450}
              data={leadData?.sourceMapArray}
              startAngle={30}
              endAngle={-330}
            >
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis
                dataKey="source"
                tick={{ fill: '#4B5563', fontSize: 12 }}
                tickLine={false}
              />

              <Tooltip
                content={({ active, payload }: TooltipProps<number, string>) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload as NormalizedSourceData;
                    return (
                      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
                        <p className="font-medium text-gray-800 mb-2">{data.source}</p>
                        <p className="text-sm text-blue-600">Total: {data.originalCount}</p>
                        <p className="text-sm text-green-500">Potential: {data.originalPotential}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Radar
                name="Total"
                dataKey="count"
                stroke="#0891B2"
                fill="#0891B2"
                fillOpacity={0.3}
                animationDuration={500}
                dot={{
                  r: 4,
                  fill: '#0891B2',
                  strokeWidth: 2,
                  stroke: '#fff'
                }}
              />
              <Radar
                name="Potential"
                dataKey="potentialCount"
                stroke="#22C55E"
                fill="#22C55E"
                fillOpacity={0.3}
                animationDuration={500}
                dot={{
                  r: 4,
                  fill: '#22C55E',
                  strokeWidth: 2,
                  stroke: '#fff'
                }}
              />
            </RadarChart>

            <div className="flex items-center justify-center text-sm gap-8 mt-6 bg-white px-6 py-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 transition-transform hover:scale-105">
                <div className="w-4 h-4 rounded-full bg-[#0891B2] opacity-70"></div>
                <span className="font-medium text-gray-700">Total</span>
              </div>
              <div className="flex items-center gap-3 transition-transform hover:scale-105">
                <div className="w-4 h-4 rounded-full bg-[#22C55E] opacity-70"></div>
                <span className="font-medium text-gray-700">Potential</span>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-1/3 flex flex-col gap-3 p-4 bg-gray-50 rounded-xl">
            <h3 className="text-lg font-medium text-gray-800 mb-2">Source Breakdown</h3>
            <div className="flex flex-col gap-3 overflow-y-auto max-h-[500px] pr-2 custom-scrollbar">
              {leadData?.sourceMapArray?.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-gray-800">{item.source}</span>
                    <span className="text-red-500 font-medium">{('originalCount' in item) ? item.originalCount : item.count}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Potential</span>
                    <span className="text-blue-500 font-medium">{('originalPotential' in item) ? item.originalPotential : item.potentialCount}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelAnalytics;